/**
 * Gallery entity menu - renders the production `ContextMenu` (ADR-0020 item
 * source) for every trigger in a scene: tab right-click, row right-click, and
 * the title-row `...`. Scenes must not hand-roll menu items; if an item is
 * missing here it is missing in production too.
 *
 * Handlers toast instead of acting.
 */
import { useState, type ComponentProps, type MouseEvent } from 'react'
import { toast } from 'sonner'
import { ContextMenu } from '@/components/features/context-menu'

export type GalleryMenuContext = ComponentProps<typeof ContextMenu>['context']

/** Approximate quiet menu width, so a button-anchored menu ends at the button. */
const MENU_WIDTH_PX = 224

type OpenMenu = { x: number; y: number; context: GalleryMenuContext }

const say = (label: string) => () => {
  toast.message(label)
}

export function useGalleryEntityMenu() {
  const [menu, setMenu] = useState<OpenMenu | null>(null)

  const openAtPointer = (event: MouseEvent, context: GalleryMenuContext) => {
    event.preventDefault()
    event.stopPropagation()
    setMenu({ x: event.clientX, y: event.clientY, context })
  }

  const openFromButton = (event: MouseEvent<HTMLElement>, context: GalleryMenuContext) => {
    event.stopPropagation()
    const rect = event.currentTarget.getBoundingClientRect()
    setMenu({ x: Math.max(8, rect.right - MENU_WIDTH_PX), y: rect.bottom + 4, context })
  }

  const node = menu ? (
    <ContextMenu
      x={menu.x}
      y={menu.y}
      context={menu.context}
      onClose={() => setMenu(null)}
      onSearchIn={say('Search in folder')}
      onCreateFile={say('New document')}
      onCreateFolder={say('New folder')}
      onImportTextFiles={say('Import files')}
      onOpen={say('Open')}
      onPreview={say('Preview')}
      onRename={say('Rename')}
      onMove={say('Move')}
      onDelete={say('Delete')}
      onAddToChat={say('Added to chat')}
      onPinToSidebar={async () => say('Pin to sidebar')()}
      onTabShowFileLocation={say('Show file location')}
      onTabRename={say('Rename')}
      onTabMove={say('Move document')}
      onTabExportPDF={say('Download PDF')}
      onTabAddToChat={say('Added to chat')}
      onTabToggleGroupCollapsed={say('Collapse group')}
      onTabPinToggle={say('Pin tab')}
      onTabClose={say('Close tab')}
      onTabCloseOthers={say('Close other tabs')}
      onTabMoveToWindow={say('Move to new window')}
      onTabCopyToWindow={say('Copy to new window')}
    />
  ) : null

  return { openAtPointer, openFromButton, isOpen: menu !== null, node }
}
